import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Check, X, RotateCcw, Ban, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ADMIN_NAV } from '../../constants/nav';
import { adminFetchRequests, adminProcessRequest, adminRevokeRequest, adminDeleteRequest } from '../../features/indicator/api/indicator';
import type { IndicatorRequest } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { INDICATOR } from '../../constants/testIds';

const SOURCE_LABEL: Record<string, string> = {
  vip: 'VIP', course_access: 'Course', manual_whitelist: 'Whitelist',
};

export default function AdminIndicatorPage() {
  const [requests, setRequests] = useState<IndicatorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<IndicatorRequest | null>(null);

  const load = () => {
    setLoading(true);
    adminFetchRequests().then(setRequests).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id: string, fn: () => Promise<any>) => {
    setBusy(id);
    try { await fn(); load(); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(null); }
  };

  const process = (r: IndicatorRequest, status: string) => {
    const note = window.prompt(`Catatan admin untuk status "${status}" (opsional):`, r.admin_note || '');
    if (note === null) return;
    act(r.id, async () => { await adminProcessRequest(r.id, status, note); toast.success(`Status: ${status}`); });
  };
  const revoke = (r: IndicatorRequest) => {
    const note = window.prompt('Alasan revoke (opsional):', '');
    if (note === null) return;
    act(r.id, async () => { await adminRevokeRequest(r.id, note); toast.success('Akses dicabut (revoked)'); });
  };
  const del = (r: IndicatorRequest) => setConfirmDel(r);
  const doDelete = () => {
    if (!confirmDel) return;
    const r = confirmDel;
    setConfirmDel(null);
    act(r.id, async () => { await adminDeleteRequest(r.id); toast.success('Permintaan dihapus'); });
  };

  const ActionBtn = ({ onClick, icon: Icon, title, cls, testId }: any) => (
    <button onClick={onClick} title={title} data-testid={testId} className={`p-2 rounded-lg transition-colors ${cls}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Indicator Access">
      <div className="max-w-5xl" data-testid={INDICATOR.adminPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Processing</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Indicator Access Requests</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : requests.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada permintaan.</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} data-testid={INDICATOR.adminRow} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-white/40">ID TradingView:</span>
                    <span className="text-[10px] uppercase tracking-wider text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2 py-0.5 rounded-full">{SOURCE_LABEL[r.eligibility_source] || r.eligibility_source}</span>
                  </div>
                  <p className="text-white font-medium break-all leading-snug mt-0.5" data-testid="indicator-tv-username">{r.tradingview_username}</p>
                  <p className="text-xs text-white/40 break-words mt-1">{r.member?.full_name} · {r.member?.email}{r.admin_note ? ` · note: ${r.admin_note}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <StatusBadge status={r.status} />
                  <div className="flex items-center gap-1 flex-wrap">
                    <ActionBtn testId={INDICATOR.processBtn} onClick={() => process(r, 'processing')} icon={Clock} title="Tandai processing" cls="text-white/60 hover:text-amber-300 hover:bg-white/5" />
                    <ActionBtn testId="indicator-revision-button" onClick={() => process(r, 'needs_revision')} icon={RotateCcw} title="Minta revisi" cls="text-white/60 hover:text-amber-300 hover:bg-white/5" />
                    <ActionBtn testId="indicator-reject-button" onClick={() => process(r, 'rejected')} icon={X} title="Tolak" cls="text-white/60 hover:text-red-400 hover:bg-white/5" />
                    <ActionBtn testId="indicator-approve-button" onClick={() => process(r, 'approved')} icon={Check} title="Setujui" cls="text-white/60 hover:text-emerald-400 hover:bg-white/5" />
                    <ActionBtn testId={INDICATOR.revokeBtn} onClick={() => revoke(r)} icon={Ban} title="Revoke akses" cls="text-white/60 hover:text-red-400 hover:bg-white/5" />
                    <ActionBtn testId="indicator-delete-button" onClick={() => del(r)} icon={Trash2} title="Hapus permintaan" cls="text-white/60 hover:text-red-400 hover:bg-white/5" />
                    {busy === r.id && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" strokeWidth={2} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!confirmDel}
        title="Hapus permintaan?"
        description={confirmDel ? `Permintaan "${confirmDel.tradingview_username}" akan dihapus permanen.` : ''}
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </DashboardLayout>
  );
}
